/**
 * Run `fn` once the first paint has settled: after the first contentful paint, once the page's largest contentful paint
 * has stopped changing for `quietMs` (the big content has arrived and been drawn), and at a quiet moment
 * (`requestIdleCallback`). A big download that can wait then does not compete with what is drawing the page, and its
 * script evaluation (about half a second for the map engine) does not land inside the paint window.
 *
 * The first perf lane found that starting the map's code at the first idle moment made Home and Schedule slower to paint,
 * and a fixed delay after the first paint (1.5 s) still put the engine's evaluation in the middle of Schedule's largest
 * paint, which then arrived late on four runs in six. Falls back to a timer when the Paint Timing API is not there, and
 * never waits longer than `maxMs` (a hidden tab, a page that keeps painting).
 */
export function afterFirstPaintSettled(fn: () => void, quietMs = 1200, maxMs = 10000): void {
  if (typeof window === 'undefined') { fn(); return; }
  let done = false;
  let fcpAt: number | null = null;
  let lastPaintAt = 0;
  const run = (): void => {
    if (done) return;
    done = true;
    observers.forEach((o) => o.disconnect());
    const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
    if (w.requestIdleCallback) w.requestIdleCallback(fn, { timeout: 3000 });
    else setTimeout(fn, 0);
  };
  const observers: PerformanceObserver[] = [];
  try {
    const watch = (type: string, onEntry: (entry: PerformanceEntry) => void): void => {
      const observer = new PerformanceObserver((list) => list.getEntries().forEach(onEntry));
      observer.observe({ type, buffered: true });
      observers.push(observer);
    };
    watch('paint', (e) => { if (e.name === 'first-contentful-paint') { fcpAt ??= performance.now(); lastPaintAt = Math.max(lastPaintAt, performance.now()); } });
    watch('largest-contentful-paint', () => { lastPaintAt = performance.now(); });
    const poll = setInterval(() => {
      if (done) { clearInterval(poll); return; }
      if (fcpAt !== null && performance.now() - lastPaintAt >= quietMs) { clearInterval(poll); run(); }
    }, 250);
  } catch { /* no Paint Timing: the timer below is the whole rule */ }
  setTimeout(run, maxMs);
}
